import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from './SubscriptionPaymentFailed.module.scss';
import LinkButton from '../../../BaseModule/Components/Button/LinkButton/LinkButton';
import {
	useReactivateExpiredSubscriptionMutation,
	useTryChargeExpiredSubscriptionMutation,
	useUserExpiredSubscriptionsQuery,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import SubscriptionPaymentWidgetWrapper from './SubscriptionPaymentWidgetWrapper';
import SubscriptionPaymentMethodSelectModal from '../SubscriptionPaymentMethodSelect/SubscriptionPaymentMethodSelectModal';
import useNotification from '../../../../Hooks/useNotification';
import useApiClient from '../../../../Hooks/useApiClient';
import FullPageLoading from '../../../BaseModule/Components/FullPageLoading';

interface Props {
	patientId: string;
	isWidget: boolean;
}

const SubscriptionPaymentFailed: React.FC<Props> = ({
	patientId,
	isWidget,
}) => {
	const { t } = useTranslation();
	const { displayGQLMutationError } = useApiClient();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const data = useUserExpiredSubscriptionsQuery({ id: patientId });
	const [reactivateExpiredSubscription] =
		useReactivateExpiredSubscriptionMutation();
	const [tryChargeExpiredSubscription] =
		useTryChargeExpiredSubscriptionMutation();
	const [isPaymentMethodSelectOpen, setIsPaymentMethodSelectOpen] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const expiredSubscription = useMemo(() => {
		return data?.data?.user?.expiredSubscriptions?.[0] ?? undefined;
	}, [data]);

	const endOfLoading = () => {
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	};

	const handleMakePaymentNow = async () => {
		setIsLoading(true);
		const result = await tryChargeExpiredSubscription({
			input: {
				userSubscriptionId: expiredSubscription?.subscription?.id,
			},
		});
		if ('error' in result) {
			if ((result.error.errors?.length || 0) > 0) {
				displayGQLMutationError(result?.error?.errors);
			}
		}
		if ('data' in result) {
			if (result.data.tryChargeExpiredSubscription?.success) {
				showNotificationAlert({
					content: t('component.subscriptionPaymentFailed.rechargeSuccess'),
				});
				// @ts-ignore
				window.location.reload();
			} else {
				console.log('failed');
				showNotificationAlert({
					content: t('component.subscriptionPaymentFailed.rechargeFailed'),
				});
			}
		}
		endOfLoading();
	};

	const handleUpdatePaymentMethod = async (paymentMethod: string) => {
		setIsLoading(true);
		const result = await reactivateExpiredSubscription({
			input: {
				userSubscriptionId: expiredSubscription?.subscription?.id,
				paymentMethod,
			},
		});
		if ('error' in result) {
			if ((result.error.errors?.length || 0) > 0) {
				displayGQLMutationError(result?.error?.errors);
			}
		}
		if ('data' in result) {
			// @ts-ignore
			window.location.href =
				result.data.reactivateExpiredSubscription?.redirectUrl;
		}
		endOfLoading();
	};

	if (
		expiredSubscription &&
		expiredSubscription?.subscription?.id &&
		expiredSubscription?.subscriptionPeriod?.id
	) {
		const component = (
			<div
				className={styles.wrapper}
				style={isWidget ? { borderRadius: '15px' } : undefined}
			>
				<div className={styles.alert}>
					<div>
						<img src='/dist/icons/alertWhole.svg' alt='Alert' />
					</div>
					<div className={styles.text}>
						{t('component.subscriptionPaymentFailed.message')}
					</div>
				</div>
				<div className={styles.controls}>
					<LinkButton
						onClick={handleMakePaymentNow}
						text='component.subscriptionPaymentFailed.makePaymentNow'
						theme='red'
					/>
					<LinkButton
						onClick={() => setIsPaymentMethodSelectOpen(true)}
						text='component.subscriptionPaymentFailed.updatePaymentMethod'
						theme='red'
					/>
				</div>
				<SubscriptionPaymentMethodSelectModal
					isOpen={isPaymentMethodSelectOpen}
					onClose={() => setIsPaymentMethodSelectOpen(false)}
					subscriptionId={expiredSubscription.subscription.id}
					subscriptionPeriodId={expiredSubscription.subscriptionPeriod.id}
					onPaymentMethodSelect={handleUpdatePaymentMethod}
				/>
				{isLoading && <FullPageLoading />}
			</div>
		);

		if (isWidget) {
			return <SubscriptionPaymentWidgetWrapper children={component} />;
		}
		return component;
	}

	return <></>;
};

export default SubscriptionPaymentFailed;
